import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, from, mergeMap, Observable, of, switchMap, take, throwError, timeout } from 'rxjs';
import { EncryptionService } from '../encryption/encryption.service';
import { environment } from '../../../../environments/environment';
import { IRefreshTokenResponse, LoginService } from 'app/services/login/login.service';
import { CVA_CONSTANTS } from 'app/util/constants';
import { MatDialog } from '@angular/material/dialog';
import { API_ROUTES } from 'app/util/route.constants';

@Injectable({
  providedIn: 'root'
})
export class ResponseInterceptorService implements HttpInterceptor {
  isRefreshing = false;
  refreshTokenSubject = new BehaviorSubject<IRefreshTokenResponse | null>(null);

  constructor(
    private readonly encryptionService: EncryptionService,
    private readonly loginService: LoginService,
    private dialog: MatDialog,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      mergeMap(event => this.decryptResponse(event)),
      catchError((error: HttpErrorResponse) => this.handleError(error, request, next))
    );
  }

  private buildErrorWithMessage(error: HttpErrorResponse, message: string): HttpErrorResponse {
    return {
      ...error,
      message,
      errorMessage: message,
      error: {
        ...(error?.error ?? {}),
        message,
        errorMessage: message,
        status: error.status,
      },
    } as HttpErrorResponse;
  }

  private getErrorCode(error: HttpErrorResponse): string {
    return error?.error?.code ?? error?.error?.error?.errorMessage ?? '';
  }

  private handleError(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const errorCode = this.getErrorCode(error);
    const isRefreshUrl = request.url.includes('/custom/refresh');
    const isLogoutUrl = request.url.includes('/custom/logout');

    if (isRefreshUrl || isLogoutUrl) {
      this.handleLogout();
      if (isRefreshUrl) {
        const message = CVA_CONSTANTS.MESSAGE.SESSION_EXPIRED;
        return throwError(() => this.buildErrorWithMessage(error, message));
      }
      return throwError(() => error);
    }

    const isTokenExpired = error.status === 401 && errorCode === 'TOKEN_EXPIRED';
    if (isTokenExpired) {
      return this.handleRefreshToken(request, next);
    }

    const statusCodes = [401, 423];
    const isRelevantStatus = statusCodes.includes(error.status) || statusCodes.includes(Number(errorCode));

    if (isRelevantStatus) {
      const isLoggedIn = this.loginService.loggedIn$.value;
      const isPreAuthEndpoint = typeof error?.url === 'string' && (
        error.url.endsWith('custom/login')
        || error.url.includes(API_ROUTES.VALIDATE_LOGIN_OTP)
        || error.url.includes(API_ROUTES.SESSION_LOGIN)
      );

      if (isLoggedIn) {
        const message = this.resolveLockedMessage(error.status, errorCode);
        this.handleLogout();
        return throwError(() => this.buildErrorWithMessage(error, message));
      }

      if (!isPreAuthEndpoint) {
        localStorage.clear();
        sessionStorage.clear();
      }
    }

    return throwError(() => error);
  }

  private resolveLockedMessage(status: number, errorCode: string): string {
    if (status !== 423) return '';
    const messages: Record<string, string> = {
      ERR_ACCOUNT_LOCKED: CVA_CONSTANTS.MESSAGE.ACCOUNT_LOCKED,
      ERR_ACCOUNT_INACTIVE: CVA_CONSTANTS.MESSAGE.INACTIVE_ACCOUNT,
    };
    return messages[errorCode] ?? '';
  }

  private decryptResponse(event: HttpEvent<unknown>): Observable<HttpEvent<unknown>> {
    if (!(event instanceof HttpResponse) || !event.url?.includes(environment.cva_url)) {
      return of(event);
    }
    return from(
      this.encryptionService.decryptAllTextFields(event.body).then(
        (decryptedBody) => event.clone({ body: decryptedBody })
      )
    );
  }

  private handleLogout(reason: string = '') {
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
    this.loginService.revokeOnLogout(true, reason);
    this.dialog.closeAll();
  }

  private handleRefreshToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ) {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(tokens => tokens != null),
        take(1),
        switchMap((tokens) => {
          if (!tokens || !this.loginService.loggedIn$.value) {
            return throwError(() => new Error('Session ended'));
          }
          const cloned =
            request.clone({
              setHeaders: {
                Authorization: `Bearer ${tokens!.idToken}`,
                'x-authorization-token': tokens!.authToken
              }
            });
          return next.handle(cloned).pipe(mergeMap(event => this.decryptResponse(event)));
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.loginService.refreshToken().pipe(
      timeout(20000),//wait till 20 sec
      switchMap((response: IRefreshTokenResponse) => {
        this.isRefreshing = false;
        const { authToken, idToken } = response;

        this.refreshTokenSubject.next(response);

        // retry original request
        const cloned = request.clone({
          setHeaders: {
            Authorization: `Bearer ${idToken}`,
            'x-authorization-token': authToken ?? ''
          }
        });

        return next.handle(cloned).pipe(mergeMap(event => this.decryptResponse(event)));
      }),

      catchError((err) => {
        this.isRefreshing = false;
        this.handleLogout(err?.errorMessage || '');
        return throwError(() => err);
      })
    );
  }
}
